<template>
    <template v-if="!isProviderOrderRequested && !isUrlOrderSelected">
        <div class="d-flex flex-column align-items-center py-2">
            <strong class="mb-2 font-size-base"> Do you want to create a palliative care provider order? </strong>
            <section class="d-flex">
                <button class="btn btn-xs btn-success" @click="requestProviderOrder()">Yes</button>
                <button class="btn btn-xs btn-danger ms-1" @click="closeRequestProviderOrder()">No</button>
            </section>
        </div>
    </template>
    <template v-else>
        <!-- SELECT ORDER FROM OPEN ORDERS SECTION -->
        <section
            id="Attach__PhysicianOrder"
            v-if="!isNewOrderRequested && !isNewOrderInProgress && isChooseOrderRequested"
        >
            <template v-if="openOrders.length > 0">
                <section class="mb-2 w-100 d-flex">
                    <strong class="font-size-sm" v-if="entity">
                        Select an order to update the patient's {{ entity }}.
                    </strong>
                    <button class="btn btn-primary btn-xs ms-auto" @click="openNewOrder()">
                        Create Provider Order
                    </button>
                </section>
                <div class="w-100 row g-0">
                    <div class="col-12 col-md-4 col-sm-6 card-wrapper" v-for="order in openOrders">
                        <PhysicianOrderCard
                            :order="order"
                            :selected-order="currentOrder"
                            :get-physicians="getPhysicians"
                            :select-order="selectOrder"
                            :open-order="openOrder"
                        >
                        </PhysicianOrderCard>
                    </div>
                </div>
                <button class="btn btn-outline-secondary btn-xs py-0 my-1 px-2" @click="closeOrderSelection()">
                    Cancel
                </button>
            </template>
            <!-- NO OPEN ORDERS -->
            <div class="d-flex flex-column align-items-center py-2" v-else>
                <strong class="mb-2 font-size-base"> There are no open Palliative Care Orders assigned to you. </strong>
                <section class="d-flex">
                    <button class="btn btn-xs btn-primary" @click="openNewOrder()">Create Provider Order</button>
                    <button class="btn btn-xs btn-outline-secondary ms-1" @click="closeOrderSelection()">Cancel</button>
                </section>
            </div>
        </section>
        <!-- ORDER SELECTED UI -->
        <section
            id="Attach__OrderSelected"
            :class="`animated alert alert-app-secondary ${
                !isChooseOrderRequested && orderId ? 'faster fadeInDown' : 'slow fadeOutUp'
            }`"
            v-if="!isChooseOrderRequested && orderId && currentOrder"
        >
            <p class="mb-0 text-dark">
                <i class="fas fa-info-circle text-app-secondary"></i>
                Updates will be added to
                <b>
                    <FormatName
                        :first-name="currentOrder.PhysicianFirstName"
                        :last-name="currentOrder.PhysicianLastName"
                        format="FN LN"
                    ></FormatName>
                </b>
                Order scheduled on <b>{{ formatDate(currentOrder.OrderDate) }}</b
                >.
            </p>
            <section class="d-flex ms-auto">
                <button
                    type="button"
                    class="btn btn-xs py-0 btn-primary"
                    @click="openOrder(currentOrder)"
                    v-if="!isUrlOrderSelected"
                >
                    View Order
                </button>
                <button type="button" class="btn btn-xs py-0 btn-primary ms-1" @click="changeOrder()">
                    Change Order
                </button>
                <button type="button" class="btn btn-xs py-0 btn-success ms-1" @click="handleExit()">
                    {{ urlNoteId ? "Return to Order" : "Done" }}
                </button>
            </section>
        </section>
        <!-- ERROR -->
        <section
            id="Attach__Error"
            :class="`animated alert alert-danger ${
                !isChooseOrderRequested && orderId ? 'faster fadeInDown' : 'slow fadeOutUp'
            }`"
            v-if="!isChooseOrderRequested && orderId && !currentOrder"
        >
            <p class="mb-0">
                <i class="fas fa-exclamation-circle text-danger"></i>
                There was a problem while adding loading the Palliative Care Provider Order selected. Please choose
                again.
            </p>
            <button type="button" class="btn table-btn btn-outline-primary ms-auto" @click="changeOrder()">
                Choose
            </button>
        </section>
        <!-- CREATE ORDER FORM -->
        <section
            id="Attach__OrderForm"
            :class="`animated ${isNewOrderRequested ? 'faster fadeInDown' : 'slow fadeOutUp'}`"
            v-if="isNewOrderRequested"
        >
            <h6 class="fw-bold" v-if="!isNewOrderInProgress">New Palliative Care Provider Order</h6>
            <FormSection v-if="!isNewOrderInProgress">
                <template #content>
                    <div class="w-100 row mb-2">
                        <div class="form-group col-12 col-md-5 offset-md-1">
                            <label class="form-check-label mb-1"> Date </label>
                            <DatePicker :getDate="onDateChange">
                                <div class="input-group date datepickerInput">
                                    <input
                                        name="order-date"
                                        type="text"
                                        class="form-control"
                                        placeholder="MM/DD/YYYY"
                                        :value="formatDateInput(orderDate)"
                                        autocomplete="off"
                                        date-format-only
                                        @blur="v$.orderDate.$touch"
                                        :class="`${!v$.orderDate.$error ? '' : 'field-invalid'}`"
                                    />
                                    <div class="input-group-addon input-group-append">
                                        <div class="input-group-text">
                                            <i class="fa fa-calendar"></i>
                                        </div>
                                    </div>
                                </div>
                            </DatePicker>
                        </div>
                        <div class="form-group col-12 col-md-5">
                            <label class="form-check-label mb-1"> Palliative Care Provider </label>
                            <TypeaheadInput
                                :getData="getPhysicians"
                                :value="orderSelectedPhysician"
                                placeholder="Type to Search Provider..."
                                :character-limit="3"
                                v-on:valueChanged="orderSelectedPhysicianChanged"
                                v-on:blurredInput="v$.orderSelectedPhysician.$touch"
                                :class="`${!v$.orderSelectedPhysician.$error ? '' : 'field-invalid'}`"
                            >
                            </TypeaheadInput>
                        </div>
                    </div>
                </template>
            </FormSection>
            <div class="text-center" v-if="!isNewOrderInProgress">
                <button type="button" class="btn btn-primary btn-xs" @click="createPhysicianOrder()">Create</button>
                <button type="button" class="btn btn-outline-secondary btn-xs ms-2" @click="closeNewOrder()">
                    Cancel
                </button>
            </div>
            <!-- LOADING -->
            <section class="p-3 d-flex flex-column text-center text-primary" v-if="isNewOrderInProgress">
                <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                <b class="mt-1 big-text">
                    <b>Creating Palliative Care Provider Order...</b>
                </b>
            </section>
        </section>
    </template>
</template>

<style src="./AttachPhysicianOrder.scss"></style>
<script lang="ts" src="./AttachPhysicianOrder.ts"></script>
